// src/features/roles/rolesSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Persona } from 'src/contstant/Persona';
import { roleService } from 'src/data/api/orbit-service/roleService';
import useUserProfile from 'src/hooks/useUserProfile';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import { ListBuRolesRequest, Role, UpdateRoleRequest } from 'src/types/orbit/Roles';
import { handleError } from 'src/utils/generalUtils';

export interface RolesState {
  rolesList: Role[];
  fetchRolesApiStatus: ApiRequestStatus;
  updateRolesApiStatus: ApiRequestStatus;
}

const initialState: RolesState = {
  rolesList: [],
  fetchRolesApiStatus: initApiStatus,
  updateRolesApiStatus: initApiStatus,
};

export const fetchRolesApiThunk = createAsyncThunk(
  'roles/fetchRoles',
  async (data: ListBuRolesRequest, { rejectWithValue }) => {
    try {
      const response =
        data.persona === Persona.PLATFORM_ADMIN
          ? await roleService.getAllDefaultRoles(data)
          : await roleService.getAllRolesbyBusinessUnit(data);
      return response.roles;
    } catch (error: any) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateRolesApiThunk = createAsyncThunk(
  'roles/updateRoles',
  async ({ data }: { data: UpdateRoleRequest }, { rejectWithValue, dispatch }) => {
    try {
      const response =
        data.persona === Persona.PLATFORM_ADMIN
          ? await roleService.update(data.programId, data)
          : await roleService.updateBusinessUnitRole(
              data.programId,
              data.businessUnitId,
              data,
            );
      dispatch(updateRoleInState({ role: response.role }));
      return { role: response.role, message: 'Role updated successfully' };
    } catch (error: any) {
      return rejectWithValue(handleError(error));
    }
  },
);

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearUpdateStatus: (state) => {
      state.updateRolesApiStatus = initApiStatus;
    },
    updateRoleInState: (state, action) => {
      const index = state.rolesList.findIndex(
        (user) => user.roleId === action.payload.role.roleId,
      );
      if (index !== -1) {
        state.rolesList[index] = action.payload.role;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRolesApiThunk.pending, (state) => {
        state.fetchRolesApiStatus.isLoading = true;
        state.fetchRolesApiStatus.errorMessage = null;
      })
      .addCase(fetchRolesApiThunk.fulfilled, (state, action) => {
        state.fetchRolesApiStatus.isLoading = false;
        state.rolesList = action.payload;
      })
      .addCase(fetchRolesApiThunk.rejected, (state, action) => {
        state.fetchRolesApiStatus.isLoading = false;
        state.fetchRolesApiStatus.errorMessage = action.payload as string;
      })

      //Update Roles

      .addCase(updateRolesApiThunk.pending, (state) => {
        state.updateRolesApiStatus.isLoading = true;
        state.updateRolesApiStatus.errorMessage = null;
        state.updateRolesApiStatus.isSuccess = false;
      })
      .addCase(updateRolesApiThunk.fulfilled, (state, action) => {
        state.updateRolesApiStatus.isLoading = false;
        state.updateRolesApiStatus.isSuccess = true;
        state.updateRolesApiStatus.successMessage = action.payload.message;
      })
      .addCase(updateRolesApiThunk.rejected, (state, action) => {
        state.updateRolesApiStatus.isLoading = false;
        state.updateRolesApiStatus.isSuccess = false;
        state.updateRolesApiStatus.errorMessage = action.payload as string;
      });
  },
});

export const { clearUpdateStatus, updateRoleInState } = rolesSlice.actions;
export default rolesSlice.reducer;
